<script lang="ts">
  import type {
    ContactLogType,
    ContactLogTypeFilter,
    TimelineCounts,
    TimelineFilter,
    TimelineItem,
  } from '@domain/timeline/model';
  import type { DialogueLog } from '@shared/types/dialogue-log';
  import { editableEntriesByEventId } from '@domain/dialogue-log/editable-entries';
  import {
    CONTACT_LOG_TYPE_FILTER_OPTIONS,
    formatDateTime,
    type DisplayEvent,
    type DisplayField,
    expandTimelineItems,
  } from '@domain/timeline/builder';

  /// 対象弁護士の折りたたみ表示件数 (v1.0 Visibility Rules「折りたたみ状態では
  /// 一部氏名と +N名 を表示してよいが、展開で全員を確認可能にする」)。
  const TARGET_LAWYERS_PREVIEW = 3;

  interface Props {
    items: TimelineItem[];
    counts?: TimelineCounts | null;
    loading?: boolean;
    error?: string | null;
    /// 弁護士 id → 表示名。 参加者行 (顧客側) の名前解決に使う。
    lawyerNameById?: Record<string, string>;
    /// 担当者 (account) id → 表示名。 商談/契約カードの詳細表示「操作者」解決に使う。
    accountNameById?: Record<string, string>;
    /// 接点ログカードに対象弁護士 (誰のログか) を表示するか。 法律事務所詳細では
    /// 所属弁護士の接点ログが混在するため表示が有用だが、 弁護士詳細では全件が
    /// 本人のログなので冗長 (86eya0dva PR5)。 未指定 (false) なら従来表示のまま。
    showContactLogLawyer?: boolean;
    /// 弁護士詳細画面で表示中の弁護士 id。設定時は商談/契約カードの「対象弁護士」
    /// 常時表示行を出さず (本人名をカード本文で繰り返さない — v1.0 Visibility
    /// Rules)、他に対象弁護士がいれば詳細表示へ「共同対象者」として回す。
    /// 未指定 (法律事務所画面) なら対象弁護士を常時表示する。
    viewerLawyerId?: string | null;
    /// フィルタ選択を親 store へ通知する。未指定ならコンポーネント内のローカル絞り込みだけで動く。
    onFilterChange?: (query: {
      type: TimelineFilter;
      contactLogType: ContactLogTypeFilter;
    }) => void;
    /// server-side filtering でまだ未取得の timeline items がある場合に親 store へ追加取得を通知する。
    onLoadMore?: () => void;
    canLoadMore?: boolean;
    /// 手動対話エントリ (kind === 'entry') の編集要求を親へ通知する。
    /// 未指定なら編集ボタンを表示しない (商談 / 契約由来カードには常に出さない)。
    onEditEntry?: (entry: DialogueLog) => void;
  }

  let {
    items,
    counts = null,
    loading = false,
    error = null,
    lawyerNameById = {},
    accountNameById = {},
    showContactLogLawyer = false,
    viewerLawyerId = null,
    onFilterChange = undefined,
    onLoadMore = undefined,
    canLoadMore = false,
    onEditEntry = undefined,
  }: Props = $props();

  // 商談/契約カードの「対象弁護士」展開状態・「詳細を表示」展開状態。
  // カード id をキーに持つだけの表示状態でありサーバへは送らない。
  let expandedTargetLawyers = $state<Record<string, boolean>>({});
  let expandedDetails = $state<Record<string, boolean>>({});

  function toggleTargetLawyers(id: string): void {
    expandedTargetLawyers = { ...expandedTargetLawyers, [id]: !expandedTargetLawyers[id] };
  }

  function toggleDetails(id: string): void {
    expandedDetails = { ...expandedDetails, [id]: !expandedDetails[id] };
  }

  /// 法律事務所画面 (viewerLawyerId 未指定) でのみ対象弁護士名の一覧を返す。
  /// 弁護士画面では常時表示行自体を出さない (別途 detailRows の「共同対象者」で扱う)。
  function targetLawyerNames(ev: DisplayEvent): string[] | null {
    if (ev.category !== 'sales_event' || viewerLawyerId) return null;
    return (ev.targetLawyers ?? []).map((l) => l.display_name ?? '対象弁護士不明');
  }

  /// 詳細表示 (展開時のみ) の行一覧。 builder が組み立てた detailFields に加えて、
  /// 操作者名の解決 (account) と、弁護士画面での共同対象者をここで合成する
  /// (どちらも「どの画面で見ているか」に依存するため component 側の責務)。
  function detailRows(ev: DisplayEvent): DisplayField[] {
    if (ev.category !== 'sales_event') return [];
    const rows: DisplayField[] = [...(ev.detailFields ?? [])];
    if (ev.actorAccountId) {
      rows.push({
        label: '操作者',
        value: accountNameById[ev.actorAccountId] ?? '操作者不明',
      });
    }
    if (viewerLawyerId && ev.targetLawyers) {
      const others = ev.targetLawyers.filter((l) => l.lawyer_id !== viewerLawyerId);
      if (others.length > 0) {
        rows.push({
          label: '共同対象者',
          value: others.map((l) => l.display_name ?? '対象弁護士不明').join('、 '),
        });
      }
    }
    return rows;
  }

  // issue #527: 全て / 接点ログ / 対話ログ / 商談・契約。 検索ボックスは設けない。
  type FilterKey = TimelineFilter;

  let activeFilter = $state<FilterKey>('all');
  let activeContactLogType = $state<ContactLogTypeFilter>('all');
  let visibleCount = $state(30);

  const displayEvents = $derived<DisplayEvent[]>(expandTimelineItems(items));

  // 編集対象 (手動対話エントリ) を表示イベント id で引く。 営業イベントは含まれない。
  const editableByEventId = $derived(editableEntriesByEventId(items));

  const localContactLogCount = $derived(
    displayEvents.filter((e) => e.category === 'contact_log').length,
  );
  const localDialogCount = $derived(
    displayEvents.filter((e) => e.category === 'dialog').length,
  );
  const localSalesEventCount = $derived(
    displayEvents.filter((e) => e.category === 'sales_event').length,
  );
  const allCount = $derived(counts?.all ?? displayEvents.length);
  const contactLogCount = $derived(counts?.contact_log ?? localContactLogCount);
  const dialogCount = $derived(counts?.dialog ?? localDialogCount);
  const salesEventCount = $derived(counts?.sales_event ?? localSalesEventCount);

  const filtered = $derived(filterEvents(displayEvents));
  const visible = $derived(filtered.slice(0, visibleCount));
  const selectedCount = $derived(selectedTotalCount());
  const hasMoreLocal = $derived(visibleCount < filtered.length);
  const hasMoreRemote = $derived(
    !!onLoadMore && canLoadMore && filtered.length < selectedCount,
  );

  function filterEvents(events: DisplayEvent[]): DisplayEvent[] {
    if (activeFilter === 'all') return events;
    const categoryFiltered = events.filter((e) => e.category === activeFilter);
    if (activeFilter !== 'contact_log' || activeContactLogType === 'all') {
      return categoryFiltered;
    }
    return categoryFiltered.filter(
      (e) => e.contactLogType === activeContactLogType,
    );
  }

  function selectFilter(key: FilterKey): void {
    activeFilter = key;
    if (key !== 'contact_log') activeContactLogType = 'all';
    visibleCount = 30;
    if (counts) {
      onFilterChange?.({
        type: key,
        contactLogType: key === 'contact_log' ? activeContactLogType : 'all',
      });
    }
  }

  function selectContactLogType(key: ContactLogTypeFilter): void {
    activeContactLogType = key;
    visibleCount = 30;
    if (counts) {
      onFilterChange?.({ type: 'contact_log', contactLogType: key });
    }
  }

  function contactLogTypeCount(type: ContactLogType): number {
    if (counts) return counts.contact_log_types[type];
    return displayEvents.filter((e) => e.contactLogType === type).length;
  }

  function selectedTotalCount(): number {
    if (!counts) return filtered.length;
    if (activeFilter === 'all') return counts.all;
    if (activeFilter === 'dialog') return counts.dialog;
    if (activeFilter === 'sales_event') return counts.sales_event;
    if (activeContactLogType === 'all') return counts.contact_log;
    return counts.contact_log_types[activeContactLogType];
  }

  function emptyMessage(): string {
    if (activeFilter === 'contact_log' && activeContactLogType !== 'all') {
      return '選択した種類の接点ログはありません。';
    }
    if (activeFilter === 'contact_log')
      return '表示できる接点ログがありません。';
    return '表示できる接点がありません。';
  }

  function loadMore(): void {
    const nextVisibleCount = visibleCount + 30;
    visibleCount = nextVisibleCount;
    if (nextVisibleCount > filtered.length && filtered.length < selectedCount) {
      onLoadMore?.();
    }
  }

  function resolveLawyer(id: string): string {
    return lawyerNameById[id] ?? id;
  }

  function customerParticipants(ev: DisplayEvent): string[] {
    return (ev.participantLawyerIds ?? []).map(resolveLawyer);
  }

  function hasParticipants(ev: DisplayEvent): boolean {
    return (ev.participantLawyerIds?.length ?? 0) > 0;
  }

  /// 接点ログの対象弁護士 id。 `showContactLogLawyer` が有効な画面 (法律事務所
  /// 詳細) でのみ、 所属弁護士経由のログ (lawyerId 非 null) に対して返す。
  function contactLogLawyerId(ev: DisplayEvent): string | null {
    if (!showContactLogLawyer || ev.category !== 'contact_log') return null;
    return ev.lawyerId ?? null;
  }

</script>

{#if loading && items.length === 0}
  <p class="status">読み込み中…</p>
{:else if error}
  <p class="status error">{error}</p>
{:else}
  <div class="filter-row" data-testid="timeline-filter-row">
    <div class="filter-chips" data-testid="timeline-filter-chips">
      <button
        type="button"
        class="chip"
        class:active={activeFilter === 'all'}
        onclick={() => selectFilter('all')}
      >
        全て ({allCount})
      </button>
      <button
        type="button"
        class="chip"
        class:active={activeFilter === 'contact_log'}
        onclick={() => selectFilter('contact_log')}
      >
        接点ログ ({contactLogCount})
      </button>
      <button
        type="button"
        class="chip"
        class:active={activeFilter === 'dialog'}
        onclick={() => selectFilter('dialog')}
        disabled={dialogCount === 0}
      >
        対話ログ ({dialogCount})
      </button>
      <button
        type="button"
        class="chip"
        class:active={activeFilter === 'sales_event'}
        onclick={() => selectFilter('sales_event')}
        disabled={salesEventCount === 0}
      >
        商談/契約 ({salesEventCount})
      </button>
    </div>
    <div class="muted small">
      {visible.length} / {selectedCount} 件表示中
    </div>
    {#if activeFilter === 'contact_log'}
      <div class="subfilter-row" data-testid="contact-log-type-filter-row">
        <button
          type="button"
          class="chip subchip"
          class:active={activeContactLogType === 'all'}
          onclick={() => selectContactLogType('all')}
        >
          すべて ({contactLogCount})
        </button>
        {#each CONTACT_LOG_TYPE_FILTER_OPTIONS as option (option.value)}
          <button
            type="button"
            class="chip subchip"
            class:active={activeContactLogType === option.value}
            onclick={() => selectContactLogType(option.value)}
          >
            {option.label} ({contactLogTypeCount(option.value)})
          </button>
        {/each}
      </div>
    {/if}
  </div>

  {#if filtered.length === 0}
    <p class="status muted">{emptyMessage()}</p>
  {:else}
    <ol class="timeline">
      {#each visible as ev (ev.id)}
        {@const editTarget = editableByEventId.get(ev.id)}
        {@const contactLogLawyer = contactLogLawyerId(ev)}
        <li class="event">
          <div class="card">
            <div class="head">
              <span class="when">{formatDateTime(ev.occurred_at)}</span>
              <span class="kind-badge">{ev.icon} {ev.typeLabel}</span>
              {#if ev.hasMinutes}
                <span class="minutes" title="議事録あり">📄</span>
              {/if}
              {#if ev.badges}
                {#each ev.badges as badge (badge)}
                  <span class="badge">{badge}</span>
                {/each}
              {/if}
              {#if onEditEntry && editTarget}
                <button
                  type="button"
                  class="edit-btn"
                  aria-label="タイムラインを編集"
                  title="タイムラインを編集"
                  data-testid="dialogue-log-edit"
                  onclick={() => onEditEntry?.(editTarget)}
                >
                  <span aria-hidden="true">✎</span>
                </button>
              {/if}
            </div>
            {#if hasParticipants(ev)}
              <div class="participants">
                <span class="side customer"
                  >{customerParticipants(ev).join('、 ')}</span
                >
              </div>
            {/if}
            {#if contactLogLawyer}
              <div class="participants" data-testid="contact-log-lawyer">
                <span class="side customer">👤 {resolveLawyer(contactLogLawyer)}</span>
              </div>
            {/if}
            <div class="title">{ev.title}</div>
            {#if ev.summary}
              <div class="summary">{ev.summary}</div>
            {/if}
            {#if ev.relatedOpportunityId}
              <div class="related">
                <span class="related-badge">🔗 関連商談</span>
              </div>
            {/if}
            {#if ev.category === 'sales_event'}
              {#if ev.primaryFields}
                <dl class="fields" data-testid="sales-event-primary-fields">
                  {#each ev.primaryFields as field (field.label)}
                    <div class="field">
                      <dt>{field.label}</dt>
                      <dd>{field.value}</dd>
                    </div>
                  {/each}
                </dl>
              {/if}
              {#if ev.conditionalFields}
                <dl class="fields" data-testid="sales-event-conditional-fields">
                  {#each ev.conditionalFields as field (field.label)}
                    <div class="field">
                      <dt>{field.label}</dt>
                      <dd>{field.value}</dd>
                    </div>
                  {/each}
                </dl>
              {/if}
              {@const lawyerNames = targetLawyerNames(ev)}
              {#if lawyerNames}
                <div class="participants target-lawyers" data-testid="target-lawyers">
                  <span class="field-label">対象弁護士：</span>
                  {#if lawyerNames.length === 0}
                    <span class="muted">未設定</span>
                  {:else}
                    {@const expanded = !!expandedTargetLawyers[ev.id]}
                    {@const visibleNames = expanded
                      ? lawyerNames
                      : lawyerNames.slice(0, TARGET_LAWYERS_PREVIEW)}
                    <span>{visibleNames.join('、 ')}</span>
                    {#if lawyerNames.length > TARGET_LAWYERS_PREVIEW}
                      <button
                        type="button"
                        class="link-btn"
                        aria-expanded={expanded}
                        onclick={() => toggleTargetLawyers(ev.id)}
                      >
                        {expanded ? '閉じる' : `+${lawyerNames.length - TARGET_LAWYERS_PREVIEW}名`}
                      </button>
                    {/if}
                  {/if}
                </div>
              {/if}
              {@const details = detailRows(ev)}
              {#if details.length > 0}
                <div class="detail-toggle">
                  <button
                    type="button"
                    class="link-btn"
                    aria-expanded={!!expandedDetails[ev.id]}
                    data-testid="detail-toggle"
                    onclick={() => toggleDetails(ev.id)}
                  >
                    {expandedDetails[ev.id] ? '詳細を閉じる' : '詳細を表示'}
                  </button>
                </div>
                {#if expandedDetails[ev.id]}
                  <dl class="fields fields-detail" data-testid="detail-fields">
                    {#each details as field (field.label)}
                      <div class="field">
                        <dt>{field.label}</dt>
                        <dd>{field.value}</dd>
                      </div>
                    {/each}
                  </dl>
                {/if}
              {/if}
            {/if}
          </div>
        </li>
      {/each}
    </ol>

    {#if hasMoreLocal || hasMoreRemote}
      <div class="more">
        <button type="button" class="btn" onclick={loadMore} disabled={loading}
          >もっと見る (+30)</button
        >
      </div>
    {/if}
  {/if}
{/if}

<style>
  .filter-row {
    display: grid;
    gap: 8px;
    margin-bottom: 12px;
    padding: 10px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #fff;
  }
  .filter-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .subfilter-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding-top: 8px;
    border-top: 1px solid #eef2f7;
  }
  .chip {
    padding: 3px 10px;
    border: 1px solid #d1d5db;
    background: #fff;
    color: #374151;
    border-radius: 999px;
    cursor: pointer;
    font-size: 0.75rem;
    font: inherit;
  }
  .chip.active {
    background: #1f2937;
    border-color: #1f2937;
    color: white;
  }
  .chip:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .subchip {
    font-size: 0.7rem;
    background: #f9fafb;
  }
  .timeline {
    list-style: none;
    margin: 0;
    padding: 0;
    position: relative;
  }
  .timeline::before {
    content: '';
    position: absolute;
    left: 8px;
    top: 0;
    bottom: 0;
    width: 1px;
    background: #e5e7eb;
  }
  .event {
    position: relative;
    padding-left: 20px;
    padding-bottom: 12px;
  }
  .card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 10px 12px;
  }
  .head {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  .when {
    color: #111827;
    font-size: 0.75rem;
    font-weight: 600;
    white-space: nowrap;
  }
  .kind-badge {
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 0.7rem;
    font-weight: 600;
    background: #eef2ff;
    color: #4338ca;
  }
  .minutes {
    font-size: 0.8rem;
  }
  /* カード右上の編集ボタン。 主要情報 (日時/カテゴリ) を邪魔しないよう右端に寄せる。 */
  .edit-btn {
    margin-left: auto;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 1px solid #d1d5db;
    background: #fff;
    border-radius: 4px;
    cursor: pointer;
    color: #4b5563;
    font-size: 0.85rem;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .edit-btn:hover {
    background: #f9fafb;
    color: #111827;
  }
  .badge {
    padding: 1px 8px;
    border-radius: 999px;
    background: #f3f4f6;
    color: #374151;
    font-size: 0.7rem;
    font-weight: 500;
  }
  .participants {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
    font-size: 0.8125rem;
    color: #374151;
    flex-wrap: wrap;
  }
  .title {
    font-weight: 500;
    margin-top: 6px;
    font-size: 0.875rem;
    color: #111827;
  }
  .summary {
    margin-top: 6px;
    font-size: 0.8125rem;
    color: #374151;
    word-break: break-word;
    /* 議事録本文の改行を保持して読めるようにする (折り返しは継続)。 */
    white-space: pre-wrap;
  }
  .fields {
    display: grid;
    gap: 3px;
    margin: 6px 0 0;
  }
  .field {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    font-size: 0.8125rem;
    color: #374151;
  }
  .field dt {
    color: #6b7280;
    white-space: nowrap;
  }
  .field dd {
    margin: 0;
    color: #111827;
    word-break: break-word;
  }
  .fields-detail {
    padding: 8px 10px;
    margin-top: 4px;
    border-radius: 4px;
    background: #f9fafb;
  }
  .target-lawyers {
    font-size: 0.8125rem;
  }
  .field-label {
    color: #6b7280;
  }
  .link-btn {
    padding: 0;
    border: 0;
    background: none;
    color: #2563eb;
    cursor: pointer;
    font: inherit;
    font-size: 0.75rem;
    text-decoration: underline;
  }
  .detail-toggle {
    margin-top: 6px;
  }
  .related {
    margin-top: 8px;
  }
  .related-badge {
    padding: 1px 8px;
    border-radius: 999px;
    background: #ecfdf5;
    color: #047857;
    font-size: 0.7rem;
    font-weight: 500;
  }
  .muted {
    color: #6b7280;
  }
  .small {
    font-size: 0.7rem;
  }
  .status {
    color: #6b7280;
    margin: 0;
  }
  .status.error {
    color: #b91c1c;
  }
  .status.muted {
    color: #9ca3af;
  }
  .more {
    text-align: center;
    margin-top: 12px;
  }
  .more button {
    padding: 4px 12px;
    border: 1px solid #d1d5db;
    background: white;
    border-radius: 4px;
    cursor: pointer;
    font: inherit;
    font-size: 0.75rem;
  }
  .more button:hover {
    background: #f9fafb;
  }
</style>
